import { createElement as h, useEffect, useRef, useState } from 'react';
import { stationaryDwell, stationaryFix, motionQuiet } from '../../lib/parkedLearning.mjs';
import './ManoeuvresPanel.css';

export default function ParkedLearningGate({ device, motion, onResume, onMovement, onRetrySensors, children }) {
  const [clock, setClock] = useState(Date.now);
  const [dwell, setDwell] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const content = useRef(null);
  const movementHandled = useRef(false);
  useEffect(() => {
    const now = Date.now();
    setClock(now);
    setDwell(previous => stationaryDwell(previous, device, now));
  }, [device]);
  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const now = Math.max(clock, Date.now());
  const stationary = stationaryFix(device, now) && dwell?.ready && motionQuiet(motion, now);
  const unlocked = Boolean(stationary && confirmed);
  useEffect(() => {
    if (!stationary) setConfirmed(false);
    if (!unlocked) {
      content.current?.querySelectorAll('video').forEach(video => video.pause());
      if (opened) window.dispatchEvent(new CustomEvent('ndg:manoeuvres-locked'));
    }
  }, [stationary, unlocked, opened]);
  useEffect(() => {
    const gpsMoving = device?.status === 'active' && Number.isFinite(device.speed) && device.speed > 0
      && Number.isFinite(device.requestedAt) && now >= device.requestedAt && now - device.requestedAt <= 15000;
    const sensorMoving = motion?.state === 'moving' && Number.isFinite(motion.capturedAt)
      && now >= motion.capturedAt && now - motion.capturedAt <= 3000;
    if (opened && (gpsMoving || sensorMoving) && !movementHandled.current) {
      movementHandled.current = true;
      onMovement?.();
    }
  }, [device, motion, now, opened, onMovement]);
  useEffect(() => () => {
    window.dispatchEvent(new CustomEvent('ndg:manoeuvres-locked'));
  }, []);
  const back = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try { await onResume(); }
    catch { setError('The route could not be updated. Try again when ready.'); }
    finally { setBusy(false); }
  };
  return h('div', { className: 'ndg-parked-learning' },
    !unlocked && h('section', { className: 'ndg-manoeuvres', 'aria-label': 'Park safely before learning' },
      h('h2', null, 'Before opening Manoeuvres'),
      h('p', null, 'Park safely, turn off the engine and remove the keys.'),
      h('p', { role: 'status' }, stationary
        ? 'GPS shows no movement and the motion sensor is quiet. Confirm the engine is off and the keys are removed.'
        : motion?.state === 'unavailable' ? 'Motion access is unavailable or was not granted. Learning stays locked.'
        : 'Waiting for fresh stationary GPS and motion-sensor readings. Learning stays locked while moving or when movement cannot be confirmed.'),
      motion?.state === 'unavailable' && onRetrySensors && h('button', { type: 'button', onClick: onRetrySensors }, 'Retry motion access'),
      h('button', { type: 'button', disabled: !stationary, onClick: () => { setConfirmed(true); setOpened(true); } }, 'Safely parked — engine off and keys removed'),
      h('button', { type: 'button', disabled: busy, onClick: back }, busy ? 'Updating route…' : 'Back to sat-nav'),
      error && h('p', { role: 'alert' }, error)),
    opened && h('div', { ref: content, hidden: !unlocked, inert: !unlocked }, children));
}
